import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@shared/lib';
import { Button, CheckIcon, SparklesIcon } from '@shared/ui';
import type { SocialRewardController } from '../model/useSocialReward';
import './SocialReward.css';

/** One compact line: what the gift is, what it takes, one button. Details live in the dialog. */
export function SocialRewardCard({
  reward,
  className,
  onOpen,
}: {
  reward: SocialRewardController;
  className?: string;
  /** Before the dialog opens: the page sends its analytics event. */
  onOpen?: () => void;
}) {
  const { t } = useTranslation();
  const titleId = useId();
  const { data, isError, isFetching, refetch } = reward.statusQuery;
  // Do not advertise a gift before a configured backend has confirmed it exists.
  if (!data?.enabled || reward.cardHidden) return null;
  const available = data.rewardStatus === 'available';
  const used = data.rewardStatus === 'used';
  const unavailable = data.rewardStatus === 'unavailable';
  const state = used ? 'used' : available ? 'ready' : 'locked';

  return (
    <section
      className={cn('sr-card', available && 'sr-card--available', used && 'sr-card--used', className)}
      aria-labelledby={titleId}
    >
      <span className="sr-card__mark" aria-hidden="true">
        {available || used ? <CheckIcon /> : <SparklesIcon />}
      </span>
      <div className="sr-card__copy">
        <h2 id={titleId} className="sr-card__title">
          {t(`socialReward.card.${state}Title`)}
        </h2>
        <p className="sr-card__hint" role={isError ? 'status' : undefined}>
          {t(isError || unavailable ? 'socialReward.card.unavailable' : `socialReward.card.${state}Hint`)}
        </p>
      </div>
      {used && (
        <button
          type="button"
          className="sr-card__close"
          onClick={reward.hideCard}
          aria-label={t('socialReward.card.hide')}
          title={t('socialReward.card.hide')}
        >
          ✕
        </button>
      )}
      {isError || unavailable ? (
        <Button variant="ghost" size="sm" className="sr-card__cta" disabled={isFetching} onClick={() => void refetch()}>
          {t('socialReward.retry')}
        </Button>
      ) : (
        !used && (
          <Button
            size="sm"
            className="sr-card__cta"
            onClick={() => {
              onOpen?.();
              reward.show();
            }}
            aria-haspopup="dialog"
          >
            {t(available ? 'socialReward.card.use' : 'socialReward.card.open')}
          </Button>
        )
      )}
    </section>
  );
}
